import { EditorView, ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { redo, undo } from "@codemirror/commands";
import { openSearchPanel } from "@codemirror/search";
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Bold,
  Code2,
  Copy,
  FileCode2,
  FilePlus2,
  FileText,
  FolderOpen,
  FolderTree,
  Heading1,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  Link2,
  List,
  ListChecks,
  Maximize2,
  Minimize2,
  Moon,
  PanelRight,
  Printer,
  Redo2,
  Save,
  Search,
  Settings,
  Strikethrough,
  Sun,
  Table2,
  Undo2,
} from "lucide-react";
import { CommandPalette, Command } from "./components/CommandPalette";
import { DocumentTabs } from "./components/DocumentTabs";
import { ExportMenu } from "./components/ExportMenu";
import { MarkdownEditor } from "./components/MarkdownEditor";
import { MarkdownPreview } from "./components/MarkdownPreview";
import { OutlinePanel } from "./components/OutlinePanel";
import { NotesPanel } from "./components/NotesPanel";
import { VaultPanel } from "./components/VaultPanel";
import { BacklinksPanel } from "./components/BacklinksPanel";
import { SettingsPanel } from "./components/SettingsPanel";
import { ToastStack } from "./components/ToastStack";
import { Toolbar } from "./components/Toolbar";
import { exportHtml, exportMarkdown, exportPdf } from "./utils/exporters";
import { pickMarkdownFile, pickSaveLocation, supportsFileSystemAccess, writeFile } from "./utils/fileSystem";
import { countWords, extractOutline, inferTitle, readingMinutes, slugifyFileName } from "./utils/markdown";
import { findBacklinks, findNoteByName } from "./utils/notes";
import {
  clearAllData,
  createId,
  loadPreferences,
  loadVaultRoot,
  loadWorkspace,
  savePreferences,
  saveVaultRoot,
  saveWorkspace,
} from "./utils/storage";
import { isTauri, pickMarkdownSavePath, type NativeFilePayload } from "./utils/desktop";
import {
  baseName,
  joinPath,
  onVaultChanged,
  pathFromDocumentId,
  pickVaultFolder,
  readVaultFile,
  relativeTo,
  scanVault,
  unwatchVault,
  vaultDocuments,
  watchVault,
  writeVaultFile,
  type VaultChange,
} from "./utils/vault";
import { OutlineItem, Preferences, Toast, VaultFile, WorkspaceDocument } from "./types";

/**
 * The on-disk file a tab is linked to. In the browser that's a File System Access
 * handle; on the desktop it's an absolute `path` written through Rust. Exactly
 * one of the two is set.
 */
interface DiskFile {
  handle?: FileSystemFileHandle;
  path?: string;
  name: string;
  content: string;
}

// Read once per page load; every state initializer below derives from it.
const initialWorkspace = loadWorkspace();
const initialDocument =
  initialWorkspace.documents.find((doc) => doc.id === initialWorkspace.activeId) ?? initialWorkspace.documents[0];

const insertions: Record<string, { before: string; after: string; fallback: string }> = {
  heading: { before: "# ", after: "", fallback: "Heading" },
  bold: { before: "**", after: "**", fallback: "bold text" },
  italic: { before: "*", after: "*", fallback: "italic text" },
  strike: { before: "~~", after: "~~", fallback: "strikethrough" },
  link: { before: "[", after: "](https://example.com)", fallback: "link text" },
  wikilink: { before: "[[", after: "]]", fallback: "Note name" },
  image: { before: "![", after: "](https://example.com/image.png)", fallback: "Alt text" },
  "unordered-list": { before: "- ", after: "", fallback: "List item" },
  "task-list": { before: "- [ ] ", after: "", fallback: "Task" },
  table: { before: "\n| Column | Column |\n| --- | --- |\n| Value | Value |\n", after: "", fallback: "" },
  code: { before: "\n```ts\n", after: "\n```\n", fallback: "const value = true;" },
};

export const App = () => {
  const [documents, setDocuments] = useState<WorkspaceDocument[]>(initialWorkspace.documents);
  const [activeDocumentId, setActiveDocumentId] = useState(initialWorkspace.activeId);
  const [markdown, setMarkdown] = useState(initialDocument.content);
  const [fileName, setFileName] = useState(initialDocument.name);
  const [preferences, setPreferences] = useState<Preferences>(loadPreferences);
  const [saveState, setSaveState] = useState<"saved" | "saving">("saved");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [, setTimeTick] = useState(0);
  const [divider, setDivider] = useState(50);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [noteQuery, setNoteQuery] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Vault: a folder on disk indexed in full, so notes that aren't open as tabs
  // still resolve as wikilinks, show up in backlinks, and are searchable.
  // Desktop only — the browser can't read a folder path.
  const [vaultRoot, setVaultRoot] = useState<string | null>(null);
  const [vaultFiles, setVaultFiles] = useState<VaultFile[]>([]);
  const [vaultScanning, setVaultScanning] = useState(false);
  // The on-disk file the ACTIVE document is linked to (File System Access API).
  // `content` mirrors what was last read from / written to disk so dirtiness
  // is derived by comparison instead of tracked through event ordering.
  // Handles for inactive tabs are parked in diskHandlesRef across switches.
  const [diskFile, setDiskFile] = useState<DiskFile | null>(null);
  const diskHandlesRef = useRef(new Map<string, DiskFile>());
  const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const renderedHtmlRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const autosaveTimer = useRef<number>();
  const firstSave = useRef(true);
  // Set while "Reset app data" reloads the page, so the beforeunload flush
  // doesn't write the workspace right back after it was cleared.
  const resettingRef = useRef(false);
  // Latest handlers for native (Tauri) menu items and file-open events. Kept in
  // a ref, refreshed every render, so the once-only listener effect always calls
  // the current closures instead of stale ones.
  const desktopRef = useRef<{
    menu: Record<string, () => void>;
    open: (file: NativeFilePayload) => void;
    update: (manual: boolean) => void;
    vaultChange: (change: VaultChange) => void;
    reopenVault: (root: string) => void;
  }>({
    menu: {},
    open: () => {},
    update: () => {},
    vaultChange: () => {},
    reopenVault: () => {},
  });

  const darkMode = preferences.theme === "dark" || (preferences.theme === "system" && systemDark);
  const stats = useMemo(() => ({ words: countWords(markdown), chars: markdown.length }), [markdown]);
  const outline = useMemo(() => extractOutline(markdown), [markdown]);
  // The active note's title lives in `fileName` (not yet flushed to `documents`),
  // so splice it in for vault-wide lookups: autocomplete, link resolution, backlinks.
  const liveDocuments = useMemo(
    () => documents.map((doc) => (doc.id === activeDocumentId ? { ...doc, name: fileName, content: markdown } : doc)),
    [documents, activeDocumentId, fileName, markdown],
  );
  // Every note the app knows about: the open tabs plus the vault files that
  // aren't open (a tab is the live version of its file, so it wins). Note
  // features read this instead of `documents` so they cover the whole folder.
  const openPaths = useMemo(
    () => new Set(liveDocuments.map((doc) => doc.path).filter((path): path is string => Boolean(path))),
    [liveDocuments],
  );
  const noteIndex = useMemo(
    () => [...liveDocuments, ...vaultDocuments(vaultFiles, openPaths)],
    [liveDocuments, vaultFiles, openPaths],
  );
  const noteNames = useMemo(() => noteIndex.map((doc) => doc.name), [noteIndex]);
  const backlinks = useMemo(
    () => findBacklinks(noteIndex, fileName, activeDocumentId),
    [noteIndex, fileName, activeDocumentId],
  );
  const activePath = documents.find((doc) => doc.id === activeDocumentId)?.path;
  const diskDirty = diskFile !== null && markdown !== diskFile.content;
  // Tabs whose linked disk file is stale. Inactive tabs froze their dirty
  // state when they were last active; only the active tab changes live.
  const dirtyTabIds = useMemo(() => {
    const ids = new Set<string>();
    for (const doc of documents) {
      const disk = doc.id === activeDocumentId ? diskFile : diskHandlesRef.current.get(doc.id);
      const content = doc.id === activeDocumentId ? markdown : doc.content;
      if (disk && content !== disk.content) ids.add(doc.id);
    }
    return ids;
  }, [documents, activeDocumentId, diskFile, markdown]);
  const exportTitle = preferences.exportTitle || inferTitle(markdown);
  const isMac = typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);
  const shortcut = (letter: string, shift = false) =>
    isMac ? `⌘${shift ? "⇧" : ""}${letter}` : `Ctrl ${shift ? "Shift " : ""}${letter}`;

  const savedLabel = () => {
    if (!lastSavedAt) return "Saved";
    const seconds = Math.floor((Date.now() - lastSavedAt) / 1000);
    if (seconds < 5) return "Saved just now";
    if (seconds < 60) return `Saved ${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Saved ${minutes}m ago`;
    return `Saved ${Math.floor(minutes / 60)}h ago`;
  };

  const showToast = (message: string, tone: Toast["tone"] = "info") => {
    const id = createId();
    setToasts((items) => [...items, { id, message, tone }]);
    window.setTimeout(() => setToasts((items) => items.filter((toast) => toast.id !== id)), 2600);
  };

  const updatePreferences = (patch: Partial<Preferences>) => {
    setPreferences((current) => {
      const next = { ...current, ...patch };
      savePreferences(next);
      return next;
    });
  };

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? "dark" : "light";
    document.documentElement.style.colorScheme = darkMode ? "dark" : "light";
  }, [darkMode]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);

  // Debounced flush of live edits into the documents array (the persistence
  // effect below writes the array to storage).
  useEffect(() => {
    // First run: content came straight from storage — no "saving" flash.
    if (firstSave.current) {
      firstSave.current = false;
      setLastSavedAt(Date.now());
      return;
    }

    setSaveState("saving");
    window.clearTimeout(autosaveTimer.current);
    autosaveTimer.current = window.setTimeout(() => {
      setDocuments((docs) => {
        const active = docs.find((doc) => doc.id === activeDocumentId);
        if (!active || (active.content === markdown && active.name === fileName)) return docs;
        return docs.map((doc) =>
          doc.id === activeDocumentId ? { ...doc, name: fileName, content: markdown, updatedAt: Date.now() } : doc,
        );
      });
      setSaveState("saved");
      setLastSavedAt(Date.now());
    }, 450);

    return () => window.clearTimeout(autosaveTimer.current);
  }, [markdown, fileName, activeDocumentId]);

  // Persist the workspace whenever the documents array or active tab changes.
  useEffect(() => {
    saveWorkspace({ documents, activeId: activeDocumentId });
  }, [documents, activeDocumentId]);

  // Keep the relative "Saved … ago" label fresh.
  useEffect(() => {
    const id = window.setInterval(() => setTimeTick((tick) => tick + 1), 15000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (resettingRef.current) return;
      // Flush live edits synchronously — the debounced autosave may not have fired.
      const next = documents.map((doc) =>
        doc.id === activeDocumentId ? { ...doc, name: fileName, content: markdown, updatedAt: Date.now() } : doc,
      );
      saveWorkspace({ documents: next, activeId: activeDocumentId });
      // The local workspace is safe, but changes not yet written to the linked
      // disk file would be silently stale — ask before leaving.
      if (diskFile && markdown !== diskFile.content) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [documents, activeDocumentId, fileName, markdown, diskFile]);

  const focusEditor = () => editorRef.current?.view?.focus();

  const handleRename = (name: string) => {
    setFileName(name);
    updatePreferences({ exportTitle: name });
  };

  const handleRenameBlur = () => {
    if (!fileName.trim()) {
      setFileName("Untitled document");
      updatePreferences({ exportTitle: "Untitled document" });
    }
  };

  const insertMarkdown = (action: string) => {
    const template = insertions[action];
    const view = editorRef.current?.view;
    if (!template || !view) return;

    const { from, to } = view.state.selection.main;
    const selected = view.state.doc.sliceString(from, to);
    const content = `${template.before}${selected || template.fallback}${template.after}`;

    view.dispatch({
      changes: { from, to, insert: content },
      selection: { anchor: from + template.before.length, head: from + template.before.length + (selected || template.fallback).length },
    });
    focusEditor();
  };

  const handleFind = () => {
    const view = editorRef.current?.view;
    if (view) openSearchPanel(view);
  };

  // Global shortcuts that must work regardless of focus.
  //  - Cmd/Ctrl+K toggles the command palette; Cmd/Ctrl+Shift+K keeps the link insert.
  //  - Cmd/Ctrl+F opens the editor's find & replace from anywhere.
  // Capture phase + stopPropagation so CodeMirror / browser defaults don't also fire.
  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      const key = event.key.toLowerCase();
      if (key === "k") {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          insertMarkdown("link");
        } else {
          setPaletteOpen((value) => !value);
        }
      } else if (key === "f" && !event.shiftKey) {
        const view = editorRef.current?.view;
        if (!view) return;
        event.preventDefault();
        event.stopPropagation();
        openSearchPanel(view);
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Desktop (Tauri) integration: relay native menu clicks to the matching
  // command and open files passed on launch or via "Open with". Subscribed once;
  // handlers are read from desktopRef so they're never stale.
  useEffect(() => {
    if (!isTauri()) return;
    let cancelled = false;
    const unlisteners: Array<() => void> = [];
    (async () => {
      const [{ listen }, { invoke }] = await Promise.all([
        import("@tauri-apps/api/event"),
        import("@tauri-apps/api/core"),
      ]);
      if (cancelled) return;
      unlisteners.push(await listen<string>("menu", (event) => desktopRef.current.menu[event.payload]?.()));
      unlisteners.push(await listen<NativeFilePayload>("open-file", (event) => desktopRef.current.open(event.payload)));
      unlisteners.push(await onVaultChanged((change) => desktopRef.current.vaultChange(change)));
      const pending = await invoke<NativeFilePayload | null>("take_pending_file");
      if (!cancelled && pending) desktopRef.current.open(pending);
      // Reopen the folder from last session so the note index is warm on launch.
      const savedVault = loadVaultRoot();
      if (!cancelled && savedVault) desktopRef.current.reopenVault(savedVault);
      // Quietly check for a newer release on launch.
      if (!cancelled) desktopRef.current.update(false);
    })();
    return () => {
      cancelled = true;
      unlisteners.forEach((unlisten) => unlisten());
    };
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (paletteOpen) return;
    if (!(event.ctrlKey || event.metaKey)) return;
    const key = event.key.toLowerCase();
    const shortcuts: Record<string, () => void> = {
      b: () => insertMarkdown("bold"),
      i: () => insertMarkdown("italic"),
      s: () => void handleSave(),
      e: () => setSettingsOpen(true),
    };

    if (shortcuts[key]) {
      event.preventDefault();
      shortcuts[key]();
    }
  };

  // Write the live editor state into the documents array (other tabs are
  // already current — only the active tab is edited in place).
  const flushActive = (docs: WorkspaceDocument[]) =>
    docs.map((doc) =>
      doc.id === activeDocumentId ? { ...doc, name: fileName, content: markdown, updatedAt: Date.now() } : doc,
    );

  // Park the active tab's disk handle so it survives tab switches.
  const parkActiveDiskHandle = () => {
    if (diskFile) diskHandlesRef.current.set(activeDocumentId, diskFile);
    else diskHandlesRef.current.delete(activeDocumentId);
  };

  const activateDocument = (target: WorkspaceDocument) => {
    parkActiveDiskHandle();
    setDiskFile(diskHandlesRef.current.get(target.id) ?? null);
    setMarkdown(target.content);
    setFileName(target.name);
    setActiveDocumentId(target.id);
    updatePreferences({ exportTitle: target.name });
  };

  const handleSelectTab = (id: string) => {
    if (id === activeDocumentId) return;
    const target = documents.find((doc) => doc.id === id);
    if (!target) return;
    setDocuments((docs) => flushActive(docs));
    activateDocument(target);
  };

  const handleNewDocument = () => {
    const fresh: WorkspaceDocument = { id: createId(), name: "Untitled document", content: "", updatedAt: Date.now() };
    setDocuments((docs) => [...flushActive(docs), fresh]);
    activateDocument(fresh);
    showToast("New document created", "success");
  };

  // Follow a `[[wikilink]]`: switch to the matching note, or create it if missing
  // (Obsidian-style). Clicking a link to the current note is a no-op.
  const handleOpenNote = (name: string) => {
    if (name.trim().toLowerCase() === fileName.trim().toLowerCase()) return;
    // Resolves against the whole vault, not just open tabs, so a link to a note
    // you've never opened lands on the real file instead of creating a duplicate.
    const existing = findNoteByName(noteIndex, name);
    if (existing) {
      handleOpenIndexed(existing.id);
      return;
    }
    const fresh: WorkspaceDocument = { id: createId(), name: name.trim(), content: `# ${name.trim()}\n\n`, updatedAt: Date.now() };
    setDocuments((docs) => [...flushActive(docs), fresh]);
    activateDocument(fresh);
    showToast(`Created note “${name.trim()}”`, "success");
  };

  // Clicking a `#tag` pill filters the Notes panel by that tag.
  const handleTagClick = (tag: string) => setNoteQuery(`#${tag}`);

  // --- Vault (desktop) ---------------------------------------------------

  /** Read a folder into the index and start watching it for outside edits. */
  const loadVault = async (root: string, announce = false) => {
    setVaultScanning(true);
    try {
      const files = await scanVault(root);
      setVaultFiles(files);
      setVaultRoot(root);
      saveVaultRoot(root);
      await watchVault(root);
      if (announce) showToast(`Vault opened — ${files.length} note${files.length === 1 ? "" : "s"}`, "success");
    } catch (error) {
      console.error("Vault scan failed:", error);
      showToast("Could not read that folder", "error");
    } finally {
      setVaultScanning(false);
    }
  };

  const handlePickVault = async () => {
    if (!isTauri()) return;
    const picked = await pickVaultFolder();
    if (picked) await loadVault(picked, true);
  };

  const handleRefreshVault = () => {
    if (vaultRoot) void loadVault(vaultRoot);
  };

  // Closing the vault only drops the index — open tabs keep their file link, so
  // Ctrl+S still writes where it did before.
  const handleCloseVault = () => {
    void unwatchVault();
    setVaultRoot(null);
    setVaultFiles([]);
    saveVaultRoot(null);
    showToast("Vault closed", "info");
  };

  /** Open a vault file as a tab, or focus the tab already showing it. */
  const openVaultPath = async (path: string) => {
    const existing = documents.find((doc) => doc.path === path);
    if (existing) {
      handleSelectTab(existing.id);
      return;
    }
    try {
      const content = await readVaultFile(path);
      const name = vaultFiles.find((file) => file.path === path)?.name ?? baseName(path).replace(/\.[^.]+$/, "");
      const doc: WorkspaceDocument = { id: createId(), name, content, updatedAt: Date.now(), path };
      // Park the link before activating: activateDocument reads this map to
      // decide what the new tab is saved to.
      diskHandlesRef.current.set(doc.id, { path, name: baseName(path), content });
      setDocuments((docs) => [...flushActive(docs), doc]);
      activateDocument(doc);
    } catch (error) {
      console.error("Could not open vault note:", error);
      showToast("Could not open that note", "error");
    }
  };

  /** Open anything in the note index — an open tab or a vault file. */
  const handleOpenIndexed = (id: string) => {
    const path = pathFromDocumentId(id);
    if (path) void openVaultPath(path);
    else handleSelectTab(id);
  };

  /**
   * Fold an outside edit back in: refresh the index, and refresh any tab showing
   * that file. A tab with unsaved edits is left alone (a warning instead) so the
   * watcher can never eat the user's work.
   */
  const handleVaultChange = async ({ changed, removed }: VaultChange) => {
    if (removed.length > 0) {
      const gone = new Set(removed);
      setVaultFiles((files) => files.filter((file) => !gone.has(file.path)));
    }
    if (changed.length === 0) return;

    const reads = await Promise.all(
      changed.map(async (path) => {
        try {
          return { path, content: await readVaultFile(path) };
        } catch {
          return null; // deleted between poll and read, or unreadable
        }
      }),
    );
    const fresh = reads.filter((entry): entry is { path: string; content: string } => entry !== null);
    if (fresh.length === 0) return;

    const root = vaultRoot;
    setVaultFiles((files) => {
      const byPath = new Map(files.map((file) => [file.path, file]));
      for (const { path, content } of fresh) {
        const existing = byPath.get(path);
        const rel = existing?.rel ?? (root ? relativeTo(root, path) : baseName(path));
        byPath.set(path, {
          path,
          rel,
          name: existing?.name ?? baseName(path).replace(/\.[^.]+$/, ""),
          content,
          updatedAt: Date.now(),
        });
      }
      return [...byPath.values()].sort((a, b) => a.rel.toLowerCase().localeCompare(b.rel.toLowerCase()));
    });

    for (const { path, content } of fresh) {
      const tab = documents.find((doc) => doc.path === path);
      if (!tab) continue;
      const isActive = tab.id === activeDocumentId;
      const link = isActive ? diskFile : diskHandlesRef.current.get(tab.id);
      // `link.content` is what we last read from / wrote to disk; if the editor
      // still matches it, nothing local would be lost by taking the new text.
      const editorContent = isActive ? markdown : tab.content;
      if (!link || link.content === content) continue;
      if (editorContent !== link.content) {
        showToast(`${baseName(path)} changed on disk — your unsaved copy was kept`, "error");
        continue;
      }
      const refreshed: DiskFile = { ...link, content };
      if (isActive) {
        setDiskFile(refreshed);
        setMarkdown(content);
      }
      diskHandlesRef.current.set(tab.id, refreshed);
      setDocuments((docs) =>
        docs.map((doc) => (doc.id === tab.id ? { ...doc, content, updatedAt: Date.now() } : doc)),
      );
    }
  };

  // Native "Search Notes" menu item: reveal the Notes panel (it's hidden in
  // full screen) and focus its search box.
  const handleFocusNotes = () => {
    setIsFullscreen(false);
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>(".notes-search input");
      input?.focus();
      input?.select();
    });
  };

  const handleCloseTab = (id: string) => {
    const target = documents.find((doc) => doc.id === id);
    if (!target) return;
    const isActive = id === activeDocumentId;
    const disk = isActive ? diskFile : diskHandlesRef.current.get(id) ?? null;
    const content = isActive ? markdown : target.content;

    // Safe to close silently only when the content lives on disk untouched.
    const safeOnDisk = disk !== null && content === disk.content;
    if (!safeOnDisk && content.trim()) {
      const message = disk
        ? `"${target.name}" has changes not saved to ${disk.name}. Close anyway?`
        : `Close "${target.name}"? It isn't saved to a file and will be removed.`;
      if (!window.confirm(message)) return;
    }

    const remaining = documents.filter((doc) => doc.id !== id);

    if (!isActive) {
      setDocuments(remaining);
    } else if (remaining.length === 0) {
      const fresh: WorkspaceDocument = { id: createId(), name: "Untitled document", content: "", updatedAt: Date.now() };
      setDocuments([fresh]);
      activateDocument(fresh);
    } else {
      const index = documents.findIndex((doc) => doc.id === id);
      setDocuments(remaining);
      activateDocument(remaining[Math.min(index, remaining.length - 1)]);
    }
    // After activation: activateDocument parks the closing tab's handle, so
    // drop it here at the end.
    diskHandlesRef.current.delete(id);
    showToast(`${target.name} closed`, "info");
  };

  const openFile = async (file: File, handle?: FileSystemFileHandle) => {
    try {
      const content = await file.text();
      const name = file.name.replace(/\.(md|markdown|txt)$/i, "");
      const doc: WorkspaceDocument = { id: createId(), name, content, updatedAt: Date.now() };
      if (handle) diskHandlesRef.current.set(doc.id, { handle, name: file.name, content });
      setDocuments((docs) => [...flushActive(docs), doc]);
      activateDocument(doc);
      showToast(`${file.name} opened in a new tab`, "success");
    } catch {
      showToast("Could not open that file", "error");
    }
  };

  const handleOpenFile = async () => {
    if (!supportsFileSystemAccess()) {
      fileInputRef.current?.click();
      return;
    }
    try {
      const handle = await pickMarkdownFile();
      if (!handle) return;
      await openFile(await handle.getFile(), handle);
    } catch {
      showToast("Could not open that file", "error");
    }
  };

  const handleFileOpen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) await openFile(file);
    event.target.value = "";
  };

  // Ctrl+S: write to the linked disk file (prompting for a location the first
  // time) where the browser supports it; otherwise download as before.
  /**
   * Desktop save: write straight back to the tab's file, prompting for a
   * location only the first time. New notes default into the open vault so they
   * join the index immediately (the watcher picks them up within a poll).
   * Returns false when there's nothing desktop-specific to do.
   */
  const saveNative = async (forcePrompt = false): Promise<boolean> => {
    if (!isTauri()) return false;
    try {
      // `activePath` is the fallback after a restart: the workspace remembers
      // which file a tab came from, but the in-memory disk link doesn't survive
      // a reload, and re-prompting for a file the user already picked is noise.
      let path = forcePrompt ? null : diskFile?.path ?? activePath ?? null;
      let docName = fileName;
      if (!path) {
        const suggested = `${slugifyFileName(exportTitle || fileName)}.md`;
        path = await pickMarkdownSavePath(vaultRoot ? joinPath(vaultRoot, suggested) : suggested);
        if (!path) return true; // cancelled — but still handled natively
        docName = baseName(path).replace(/\.(md|markdown|txt)$/i, "");
        setFileName(docName);
        updatePreferences({ exportTitle: docName });
      }
      await writeVaultFile(path, markdown);
      const disk: DiskFile = { path, name: baseName(path), content: markdown };
      setDiskFile(disk);
      diskHandlesRef.current.set(activeDocumentId, disk);
      setDocuments((docs) =>
        docs.map((doc) =>
          doc.id === activeDocumentId
            ? { ...doc, name: docName, content: markdown, path: path as string, updatedAt: Date.now() }
            : doc,
        ),
      );
      showToast(`Saved to ${baseName(path)}`, "success");
    } catch (error) {
      console.error("Native save failed:", error);
      showToast("Could not save to disk", "error");
    }
    return true;
  };

  const handleSave = async () => {
    const name = exportTitle || fileName;
    if (await saveNative()) return;
    if (supportsFileSystemAccess()) {
      try {
        let handle = diskFile?.handle ?? null;
        let docName = fileName;
        if (!handle) {
          handle = await pickSaveLocation(`${slugifyFileName(name)}.md`);
          if (!handle) return; // user cancelled
          docName = handle.name.replace(/\.(md|markdown|txt)$/i, "");
          setFileName(docName);
          updatePreferences({ exportTitle: docName });
        }
        await writeFile(handle, markdown);
        const disk: DiskFile = { handle, name: handle.name, content: markdown };
        setDiskFile(disk);
        diskHandlesRef.current.set(activeDocumentId, disk);
        setDocuments((docs) =>
          docs.map((doc) =>
            doc.id === activeDocumentId ? { ...doc, name: docName, content: markdown, updatedAt: Date.now() } : doc,
          ),
        );
        showToast(`Saved to ${handle.name}`, "success");
        return;
      } catch {
        showToast("Could not save to disk — downloading instead", "error");
      }
    }
    handleDownloadMarkdown();
  };

  // Save As: always prompt for a fresh location, then relink the active tab to it.
  const handleSaveAs = async () => {
    if (isTauri()) {
      await saveNative(true);
      return;
    }
    if (!supportsFileSystemAccess()) {
      handleDownloadMarkdown();
      return;
    }
    try {
      const handle = await pickSaveLocation(`${slugifyFileName(exportTitle || fileName)}.md`);
      if (!handle) return;
      const docName = handle.name.replace(/\.(md|markdown|txt)$/i, "");
      await writeFile(handle, markdown);
      const disk: DiskFile = { handle, name: handle.name, content: markdown };
      setDiskFile(disk);
      diskHandlesRef.current.set(activeDocumentId, disk);
      setFileName(docName);
      updatePreferences({ exportTitle: docName });
      setDocuments((docs) =>
        docs.map((doc) =>
          doc.id === activeDocumentId ? { ...doc, name: docName, content: markdown, updatedAt: Date.now() } : doc,
        ),
      );
      showToast(`Saved to ${handle.name}`, "success");
    } catch {
      showToast("Could not save to disk", "error");
    }
  };

  // Open a file the desktop app handed us (launch arg or "Open with"). The tab is
  // linked to the path it came from, so Ctrl+S writes straight back to that file
  // instead of asking where to put it.
  const openNativeFile = (payload: NativeFilePayload) => {
    const alreadyOpen = documents.find((doc) => doc.path === payload.path);
    if (alreadyOpen) {
      handleSelectTab(alreadyOpen.id);
      showToast(`${payload.name} is already open`, "info");
      return;
    }
    const name = payload.name.replace(/\.(md|markdown|txt|mdown|mkd|mdtext)$/i, "");
    const doc: WorkspaceDocument = {
      id: createId(),
      name,
      content: payload.content,
      updatedAt: Date.now(),
      path: payload.path,
    };
    diskHandlesRef.current.set(doc.id, { path: payload.path, name: payload.name, content: payload.content });
    setDocuments((docs) => [...flushActive(docs), doc]);
    activateDocument(doc);
    showToast(`${payload.name} opened`, "success");
  };

  // Desktop auto-update. `manual` = triggered from the Help menu (report "up to
  // date" / errors); otherwise it's the silent launch check that only speaks up
  // when an update is actually available.
  const runUpdateCheck = async (manual: boolean) => {
    try {
      const { check } = await import("@tauri-apps/plugin-updater");
      const update = await check();
      if (!update) {
        if (manual) showToast("You're on the latest version", "success");
        return;
      }
      const proceed = window.confirm(
        `Markdown Studio ${update.version} is available${update.body ? `:\n\n${update.body}` : "."}` +
          `\n\nDownload and install now? The app will restart when it's done.`,
      );
      if (!proceed) return;
      showToast("Downloading update…", "info");
      await update.downloadAndInstall();
      const { relaunch } = await import("@tauri-apps/plugin-process");
      await relaunch();
    } catch (err) {
      console.error("Update check failed:", err);
      if (manual) showToast("Could not check for updates", "error");
    }
  };

  const handleDownloadMarkdown = async () => {
    if (await exportMarkdown(markdown, exportTitle || fileName)) showToast("Markdown downloaded", "success");
  };

  const handleExportHtml = async () => {
    const saved = await exportHtml(renderedHtmlRef.current?.innerHTML ?? "", {
      title: exportTitle,
      theme: preferences.exportTheme,
    });
    if (saved) showToast("HTML exported", "success");
  };

  const handleResetApp = () => {
    const confirmed = window.confirm(
      "Reset Markdown Studio? All tabs, documents, and settings stored in this browser will be removed. Files saved to disk are not affected.",
    );
    if (!confirmed) return;
    resettingRef.current = true;
    clearAllData();
    window.location.reload();
  };

  const handleEditorDrop = async (files: FileList) => {
    const list = Array.from(files);
    const documentFile = list.find(
      (file) => /\.(md|markdown|txt)$/i.test(file.name) || file.type === "text/markdown" || file.type === "text/plain",
    );
    if (documentFile) {
      await openFile(documentFile);
      return;
    }

    const images = list.filter((file) => file.type.startsWith("image/"));
    if (!images.length) {
      showToast("Drop images or Markdown files here", "error");
      return;
    }

    for (const image of images) {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(image);
      });
      const view = editorRef.current?.view;
      const insert = `\n![${image.name}](${dataUrl})\n`;
      if (view) {
        const cursor = view.state.selection.main.head;
        view.dispatch({ changes: { from: cursor, insert } });
      } else {
        setMarkdown((current) => `${current}${insert}`);
      }
    }
    showToast("Image inserted", "success");
  };

  const handleCopyHtml = async () => {
    try {
      const html = renderedHtmlRef.current?.innerHTML ?? "";
      await navigator.clipboard.writeText(html);
      showToast("Rendered HTML copied", "success");
    } catch {
      showToast("Clipboard permission was denied", "error");
    }
  };

  const syncPreviewScroll = (ratio: number) => {
    if (!preferences.syncScroll) return;
    const preview = previewRef.current;
    if (!preview || !previewVisible) return;
    const max = preview.scrollHeight - preview.clientHeight;
    preview.scrollTop = Math.max(0, max * ratio);
  };

  const handleOutlineSelect = (item: OutlineItem) => {
    const view = editorRef.current?.view;
    if (view) {
      const line = view.state.doc.line(Math.min(item.line + 1, view.state.doc.lines));
      view.dispatch({
        selection: { anchor: line.from },
        effects: EditorView.scrollIntoView(line.from, { y: "start", yMargin: 12 }),
      });
      view.focus();
    }

    const container = previewRef.current;
    if (!container) return;
    const index = outline.indexOf(item);
    // Double rAF: let the editor jump (and its ratio-based preview sync) settle
    // first, then land the preview precisely on the heading.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const headings = container.querySelectorAll<HTMLElement>(
          ".markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4, .markdown-body h5, .markdown-body h6",
        );
        headings[index]?.scrollIntoView({ block: "start" });
      }),
    );
  };

  // Make in-document anchor links (e.g. a table of contents) scroll to the matching
  // heading inside the preview pane instead of doing nothing / jumping the window.
  const handlePreviewClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest("a");
    const href = anchor?.getAttribute("href") ?? "";
    if (!href.startsWith("#") || href.length < 2) return;

    const container = previewRef.current;
    if (!container) return;

    const id = decodeURIComponent(href.slice(1));
    let target: HTMLElement | null = null;
    try {
      target = container.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    } catch {
      target = null;
    }
    if (!target) return;

    event.preventDefault();
    // scrollIntoView scrolls whichever element actually scrolls (the page here, or the
    // preview container if it ever becomes the scroller). scroll-margin-top on headings
    // keeps the target clear of the sticky toolbar.
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  };

  const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
    const container = event.currentTarget.parentElement;
    if (!container) return;
    event.currentTarget.setPointerCapture(event.pointerId);

    const onMove = (moveEvent: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const next = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setDivider(Math.min(72, Math.max(28, next)));
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const step = event.key === "ArrowLeft" ? -2 : 2;
    setDivider((current) => Math.min(72, Math.max(28, current + step)));
  };

  const runCommand = (command: "undo" | "redo") => {
    const view = editorRef.current?.view;
    if (!view) return;
    (command === "undo" ? undo : redo)(view);
    focusEditor();
  };

  const handleExportPdf = async () => {
    const element = renderedHtmlRef.current;
    if (!element) return;
    try {
      await exportPdf(element, {
        title: exportTitle,
        pageSize: preferences.pageSize,
        margins: preferences.margins,
        includePageNumbers: preferences.includePageNumbers,
      });
      showToast("PDF exported", "success");
    } catch {
      showToast("PDF export failed", "error");
    }
  };

  const commands: Command[] = [
    // File
    { id: "new", group: "File", label: "New document", icon: <FilePlus2 size={16} />, keywords: "create blank reset", run: handleNewDocument },
    { id: "open", group: "File", label: "Open Markdown file…", icon: <FolderOpen size={16} />, keywords: "import upload load disk", run: handleOpenFile },
    ...(isTauri()
      ? [
          {
            id: "open-folder",
            group: "File",
            label: "Open folder as vault…",
            icon: <FolderTree size={16} />,
            keywords: "vault directory notes index folder workspace",
            run: () => void handlePickVault(),
          },
        ]
      : []),
    { id: "save", group: "File", label: "Save", icon: <Save size={16} />, hint: shortcut("S"), keywords: "write disk file download md", run: handleSave },

    // Edit
    { id: "find", group: "Edit", label: "Find & replace", icon: <Search size={16} />, hint: shortcut("F"), keywords: "search replace regex", run: handleFind },
    { id: "undo", group: "Edit", label: "Undo", icon: <Undo2 size={16} />, run: () => runCommand("undo") },
    { id: "redo", group: "Edit", label: "Redo", icon: <Redo2 size={16} />, run: () => runCommand("redo") },
    { id: "copy-html", group: "Edit", label: "Copy rendered HTML", icon: <Copy size={16} />, keywords: "clipboard", run: handleCopyHtml },

    // Insert
    { id: "i-heading", group: "Insert", label: "Heading", icon: <Heading1 size={16} />, keywords: "title h1", run: () => insertMarkdown("heading") },
    { id: "i-bold", group: "Insert", label: "Bold", icon: <Bold size={16} />, hint: shortcut("B"), keywords: "strong", run: () => insertMarkdown("bold") },
    { id: "i-italic", group: "Insert", label: "Italic", icon: <Italic size={16} />, hint: shortcut("I"), keywords: "emphasis", run: () => insertMarkdown("italic") },
    { id: "i-strike", group: "Insert", label: "Strikethrough", icon: <Strikethrough size={16} />, keywords: "strike delete", run: () => insertMarkdown("strike") },
    { id: "i-link", group: "Insert", label: "Link", icon: <LinkIcon size={16} />, hint: shortcut("K", true), keywords: "url href anchor", run: () => insertMarkdown("link") },
    { id: "i-wikilink", group: "Insert", label: "Wiki link to note", icon: <Link2 size={16} />, keywords: "wikilink internal note backlink obsidian [[", run: () => insertMarkdown("wikilink") },
    { id: "i-image", group: "Insert", label: "Image", icon: <ImageIcon size={16} />, keywords: "picture photo", run: () => insertMarkdown("image") },
    { id: "i-ul", group: "Insert", label: "Bullet list", icon: <List size={16} />, keywords: "unordered bullets", run: () => insertMarkdown("unordered-list") },
    { id: "i-task", group: "Insert", label: "Task list", icon: <ListChecks size={16} />, keywords: "todo checkbox checklist", run: () => insertMarkdown("task-list") },
    { id: "i-table", group: "Insert", label: "Table", icon: <Table2 size={16} />, keywords: "grid rows columns", run: () => insertMarkdown("table") },
    { id: "i-code", group: "Insert", label: "Code block", icon: <Code2 size={16} />, keywords: "fence snippet", run: () => insertMarkdown("code") },

    // View
    { id: "v-preview", group: "View", label: previewVisible ? "Hide preview" : "Show preview", icon: <PanelRight size={16} />, keywords: "split pane toggle", run: () => setPreviewVisible((value) => !value) },
    {
      id: "v-fullscreen",
      group: "View",
      label: isFullscreen ? "Exit full screen" : "Enter full screen",
      icon: isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />,
      keywords: "zen focus distraction free",
      run: () => setIsFullscreen((value) => !value),
    },
    {
      id: "v-theme",
      group: "View",
      label: darkMode ? "Switch to light theme" : "Switch to dark theme",
      icon: darkMode ? <Sun size={16} /> : <Moon size={16} />,
      keywords: "dark light appearance mode",
      run: () => updatePreferences({ theme: darkMode ? "light" : "dark" }),
    },
    { id: "v-settings", group: "View", label: "Open settings", icon: <Settings size={16} />, hint: shortcut("E"), keywords: "preferences options", run: () => setSettingsOpen(true) },

    // Export
    { id: "e-pdf", group: "Export", label: "Export as PDF", icon: <Printer size={16} />, keywords: "download print document", run: handleExportPdf },
    {
      id: "e-html",
      group: "Export",
      label: "Export as HTML",
      icon: <FileCode2 size={16} />,
      keywords: "download web page",
      run: handleExportHtml,
    },
    { id: "e-md", group: "Export", label: "Export as Markdown", icon: <FileText size={16} />, keywords: "download md file copy", run: handleDownloadMarkdown },
  ];

  // Refresh the native-menu dispatch table every render so the Tauri listener
  // (subscribed once) always invokes current closures. Ids match lib.rs.
  desktopRef.current = {
    menu: {
      new: handleNewDocument,
      open: () => void handleOpenFile(),
      save: () => void handleSave(),
      save_as: () => void handleSaveAs(),
      export_pdf: () => void handleExportPdf(),
      export_html: () => void handleExportHtml(),
      export_md: () => void handleDownloadMarkdown(),
      undo: () => runCommand("undo"),
      redo: () => runCommand("redo"),
      find: handleFind,
      insert_wikilink: () => insertMarkdown("wikilink"),
      new_note: handleNewDocument,
      focus_notes: handleFocusNotes,
      open_folder: () => void handlePickVault(),
      copy_html: () => void handleCopyHtml(),
      toggle_preview: () => setPreviewVisible((value) => !value),
      toggle_theme: () => updatePreferences({ theme: darkMode ? "light" : "dark" }),
      fullscreen: () => setIsFullscreen((value) => !value),
      settings: () => setSettingsOpen(true),
      check_updates: () => void runUpdateCheck(true),
    },
    open: openNativeFile,
    update: runUpdateCheck,
    vaultChange: (change) => void handleVaultChange(change),
    reopenVault: (root) => void loadVault(root),
  };

  return (
    <div className={isFullscreen ? "app fullscreen" : "app"} onKeyDown={handleKeyDown}>
      <Toolbar
        theme={darkMode ? "dark" : "light"}
        onFormat={insertMarkdown}
        onOpenCommandPalette={() => setPaletteOpen(true)}
        onNew={handleNewDocument}
        onOpen={handleOpenFile}
        onSave={handleSave}
        onUndo={() => runCommand("undo")}
        onRedo={() => runCommand("redo")}
        onFind={handleFind}
        onCopyHtml={handleCopyHtml}
        onToggleFullscreen={() => setIsFullscreen((value) => !value)}
        onOpenSettings={() => setSettingsOpen(true)}
        onToggleTheme={() => updatePreferences({ theme: darkMode ? "light" : "dark" })}
        onTogglePreview={() => setPreviewVisible((value) => !value)}
        previewVisible={previewVisible}
        isFullscreen={isFullscreen}
      />
      <input ref={fileInputRef} className="visually-hidden" type="file" accept=".md,text/markdown,text/plain" onChange={handleFileOpen} />

      <main className="workspace">
        {!isFullscreen && (
          <div className="side-panels">
            {isTauri() && (
              <VaultPanel
                root={vaultRoot}
                files={vaultFiles}
                activePath={activePath}
                scanning={vaultScanning}
                onPick={() => void handlePickVault()}
                onOpenFile={(path) => void openVaultPath(path)}
                onRefresh={handleRefreshVault}
                onClose={handleCloseVault}
              />
            )}
            <NotesPanel
              documents={noteIndex}
              activeId={activeDocumentId}
              query={noteQuery}
              onQueryChange={setNoteQuery}
              onOpen={handleOpenIndexed}
              onNew={handleNewDocument}
            />
            <OutlinePanel items={outline} onSelect={handleOutlineSelect} />
            <BacklinksPanel backlinks={backlinks} onOpen={handleOpenIndexed} />
            <ExportMenu
              pageSize={preferences.pageSize}
              margins={preferences.margins}
              title={exportTitle}
              includePageNumbers={preferences.includePageNumbers}
              exportTheme={preferences.exportTheme}
              onChange={({ title, ...rest }) => {
                updatePreferences(title === undefined ? rest : { ...rest, exportTitle: title });
                if (title !== undefined) setFileName(title);
              }}
              onPdf={handleExportPdf}
              onHtml={handleExportHtml}
              onMarkdown={handleDownloadMarkdown}
            />
          </div>
        )}

        <div className="editor-column">
          {!isFullscreen && (
            <DocumentTabs
              documents={documents}
              activeId={activeDocumentId}
              dirtyIds={dirtyTabIds}
              onSelect={handleSelectTab}
              onClose={handleCloseTab}
              onNew={handleNewDocument}
            />
          )}
          <section className="editor-preview-grid" style={{ ["--divider" as string]: `${previewVisible ? divider : 100}%` }}>
          <section className="pane editor-pane" aria-label="Markdown editor panel">
            <div className="pane-header">
              <div className="pane-header-left">
                <input
                  className="doc-title"
                  value={fileName}
                  size={Math.min(Math.max(fileName.length + 1, 8), 42)}
                  onChange={(event) => handleRename(event.target.value)}
                  onBlur={handleRenameBlur}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                  aria-label="Document title"
                  spellCheck={false}
                />
                <span
                  className={`save-status ${diskDirty ? "dirty" : saveState}`}
                  aria-live="polite"
                  title={diskFile ? `Drafts auto-save locally · ${shortcut("S")} writes to ${diskFile.name}` : undefined}
                >
                  <span className="save-dot" />
                  {diskFile
                    ? diskDirty
                      ? `${diskFile.name} — unsaved`
                      : `Saved to ${diskFile.name}`
                    : saveState === "saving"
                      ? "Saving…"
                      : savedLabel()}
                </span>
              </div>
              <span className="pane-stats">
                {stats.words} words · {stats.chars} chars
                {stats.words > 0 && ` · ~${readingMinutes(stats.words)} min read`}
              </span>
            </div>
            <MarkdownEditor
              ref={editorRef}
              value={markdown}
              darkMode={darkMode}
              fontSize={preferences.editorFontSize}
              tabSize={preferences.tabSize}
              showLineNumbers={preferences.showLineNumbers}
              wordWrap={preferences.wordWrap}
              spellcheck={preferences.spellcheck}
              isFullscreen={isFullscreen}
              noteNames={noteNames}
              onChange={setMarkdown}
              onScroll={syncPreviewScroll}
              onDropFiles={handleEditorDrop}
            />
          </section>

          {previewVisible && (
            <>
              <div
                className="resize-divider"
                role="separator"
                aria-label="Resize editor and preview"
                aria-orientation="vertical"
                aria-valuenow={Math.round(divider)}
                aria-valuemin={28}
                aria-valuemax={72}
                tabIndex={0}
                onPointerDown={handleResizeStart}
                onKeyDown={handleResizeKeyDown}
              />
              <section className="pane preview-pane" aria-label="Live Markdown preview">
                <div className="pane-header">
                  <span>Preview</span>
                  <span>GitHub style</span>
                </div>
                <div className="preview-scroll" ref={previewRef} onClick={handlePreviewClick}>
                  <div ref={renderedHtmlRef}>
                    <MarkdownPreview
                      markdown={markdown}
                      darkMode={darkMode}
                      fontSize={preferences.previewFontSize}
                      width={preferences.previewWidth}
                      font={preferences.previewFont}
                      enableMath={preferences.enableMath}
                      noteNames={noteNames}
                      onWikiLink={handleOpenNote}
                      onTagClick={handleTagClick}
                    />
                  </div>
                </div>
              </section>
            </>
          )}
          </section>
        </div>
      </main>

      <SettingsPanel
        open={settingsOpen}
        preferences={preferences}
        onClose={() => setSettingsOpen(false)}
        onChange={updatePreferences}
        onReset={handleResetApp}
      />
      <CommandPalette open={paletteOpen} commands={commands} onClose={() => setPaletteOpen(false)} />
      <ToastStack toasts={toasts} />
    </div>
  );
};
